import type { ChangeEventHandler } from 'react'
import { Fragment, useCallback, useState, useMemo } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'
import config from 'constants/config'
import ExperimentItem from './ExperimentItem'
import ChertoExperimentItem from './ChertoExperimentItem'
import FeatureFlagItem from './FeatureFlagItem'
import SearchFormTextField from 'components/SearchV2/Components/SearchFormField/SearchFormTextField'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { EmptyObject, objectEntries } from 'lib/object/objectUtils'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import BodyText from 'LuxKit/Typography/BodyText'
import Divider from 'LuxKit/Divider'
import { sortBy } from 'lib/array/arrayUtils'
import ExperimentPanelSection from './ExperimentPanelSection'
import type { ChertoExperimentEntry, ExperimentEntry, FeatureFlagEntry } from './ExperimentPanelHelpers'
import { filterExperimentEntry, getChertoExperimentConfig } from './ExperimentPanelHelpers'

function ExperimentControlPanel() {
  const optimizelyFeatureFlags = useAppSelector(state => state.optimizely.optimizelyFeatureFlags ?? EmptyObject)
  const optimizelyExperiments = useAppSelector(state => state.optimizely.optimizelyExperiments ?? EmptyObject)
  const chertoExperiments = useAppSelector(state => state.cherto.chertoExperiments ?? EmptyObject)
  const [searchValue, setSearchValue] = useState('')

  const experimentList = useMemo<Array<ExperimentEntry>>(() => {
    return sortBy(
      objectEntries(optimizelyExperiments),
      ([id]) => id,
      'asc',
    ).filter((entry): entry is ExperimentEntry => {
      const [, exp] = entry
      return exp !== undefined
    })
  }, [optimizelyExperiments])

  const chertoExperimentList = useMemo<Array<ChertoExperimentEntry>>(() => {
    return sortBy(
      (config.CHERTO_EXPERIMENTS ?? []).flatMap((experimentId) => {
        const experimentConfig = getChertoExperimentConfig(experimentId)
        if (!experimentConfig) {
          return []
        }

        return [[experimentId, chertoExperiments[experimentId], experimentConfig] as ChertoExperimentEntry]
      }),
      ([experimentId]) => experimentId,
      'asc',
    )
  }, [chertoExperiments])

  const featureFlagList = useMemo<Array<FeatureFlagEntry>>(() => {
    return sortBy(
      objectEntries(optimizelyFeatureFlags),
      ([id]) => id,
      'asc',
    ).filter((entry): entry is FeatureFlagEntry => entry !== undefined)
  }, [optimizelyFeatureFlags])

  const filteredExperiments = useMemo<Array<ExperimentEntry>>(() => {
    return experimentList.filter(filterExperimentEntry(searchValue))
  }, [searchValue, experimentList])

  const filteredChertoExperiments = useMemo<Array<ChertoExperimentEntry>>(() => {
    return chertoExperimentList.filter(filterExperimentEntry(searchValue))
  }, [chertoExperimentList, searchValue])

  const filteredFeatureFlags = useMemo<Array<FeatureFlagEntry>>(() => {
    return featureFlagList.filter(filterExperimentEntry(searchValue))
  }, [featureFlagList, searchValue])

  const hasFeatureFlags = Object.keys(optimizelyFeatureFlags).length > 0
  const hasExperiments = Object.keys(optimizelyExperiments).length > 0
  const hasChertoExperiments = chertoExperimentList.length > 0

  const handleClearSearch = useCallback(() => {
    setSearchValue('')
  }, [])

  const handleSearchChange = useCallback<ChangeEventHandler<HTMLInputElement>>((e) => {
    setSearchValue(e.target.value)
  }, [])

  return (
    <ModalBase mode="drawer-starboard">
      <ModalHeader title="AB Control Panel">
        <SearchFormTextField
          label="Search experiments"
          placeholder="search by key or name"
          value={searchValue}
          onChange={handleSearchChange}
          onClear={handleClearSearch}
        />
      </ModalHeader>
      <ModalBody>
        <ModalContent>
          <VerticalSpacer gap={12}>
            <ExperimentPanelSection heading="Experiments" defaultOpen>
              {!hasExperiments && <BodyText variant="large">No experiments found.</BodyText>}

              {hasExperiments && (
                <VerticalSpacer gap={12}>
                  {filteredExperiments.length === 0 && <BodyText variant="large">No matching experiments</BodyText>}
                  {filteredExperiments.map(([experimentId, experiment], index) => (
                    <Fragment key={experimentId}>
                      {index > 0 && <Divider kind="secondary" />}
                      <ExperimentItem
                        experimentId={experimentId}
                        experiment={experiment}
                      />
                    </Fragment>
                  ))}
                </VerticalSpacer>
              )}
            </ExperimentPanelSection>
            <ExperimentPanelSection heading="Cherto Experiments" defaultOpen>
              {!hasChertoExperiments && <BodyText variant="large">No Cherto experiments found.</BodyText>}

              {hasChertoExperiments && (
                <VerticalSpacer gap={12}>
                  {filteredChertoExperiments.length === 0 && <BodyText variant="large">No matching Cherto experiments</BodyText>}
                  {filteredChertoExperiments.map(([experimentId, experiment, experimentConfig], index) => (
                    <Fragment key={experimentId}>
                      {index > 0 && <Divider kind="secondary" />}
                      <ChertoExperimentItem
                        experimentId={experimentId}
                        experiment={experiment}
                        experimentConfig={experimentConfig}
                      />
                    </Fragment>
                  ))}
                </VerticalSpacer>
              )}
            </ExperimentPanelSection>
            <ExperimentPanelSection heading="Feature Flags" defaultOpen>
              {!hasFeatureFlags && <BodyText variant="large">No feature flags found.</BodyText>}

              {hasFeatureFlags && (
                <VerticalSpacer gap={12}>
                  {filteredFeatureFlags.length === 0 && <BodyText variant="large">No matching feature flags</BodyText>}
                  {filteredFeatureFlags.map(([flagKey, enabled], index) => (
                    <Fragment key={flagKey}>
                      {index > 0 && <Divider kind="secondary" />}
                      <FeatureFlagItem flagKey={flagKey} enabled={enabled} />
                    </Fragment>
                  ))}
                </VerticalSpacer>
              )}
            </ExperimentPanelSection>
          </VerticalSpacer>
        </ModalContent>
      </ModalBody>
    </ModalBase>
  )
}

export default ExperimentControlPanel
